import React from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000ResponsiveToolbar from '../../../u2000Core/components/toolbar/U2000ResponsiveToolbar';
import { FaPlus, FaRegFile, FaTrashAlt } from 'react-icons/fa';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import type { U2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { U2000FormStateEnumState } from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { NS, U3208ROLES } from '../../U3208';
import { u3208IsSystemCorpus, type U3208AiRagCorpusFormRow } from '../../U3208Service';
import { u2000CanManageScopeAwareEntity } from '../../../u2000Core/tools/U2000SystemScopeAccess';

interface U3208AiRagCorpusFormToolBarProps {
  formStateHandlers: U2000FormStateHandlers<U3208AiRagCorpusFormRow> & {
    getData: () => U3208AiRagCorpusFormRow | null;
    getFormState: () => U2000FormStateEnumState;
    addNewCorpus: () => void;
    modifyCorpus: () => void;
    deleteCorpus: () => void;
  };
}

export const U3208AiRagCorpusFormToolBar: React.FC<U3208AiRagCorpusFormToolBarProps> = ({
  formStateHandlers,
}) => {
  const formState = formStateHandlers.getFormState();
  const data = formStateHandlers.getData();
  const isSystem = data ? u3208IsSystemCorpus(data) : false;
  const { auth, hasRole } = useU2000AuthContext();
  const { t } = useTranslation();
  const canEditCorpus = u2000CanManageScopeAwareEntity({
    isSystemScope: isSystem,
    hasRequiredRole: hasRole(U3208ROLES.EDIT),
    sessionOrganizationId: auth.user.organizationId,
  });
  const canDeleteCorpus = u2000CanManageScopeAwareEntity({
    isSystemScope: isSystem,
    hasRequiredRole: hasRole(U3208ROLES.DELETE),
    sessionOrganizationId: auth.user.organizationId,
  });

  const actions = [
    {
      key: 'add',
      label: t('btnAdd', { ns: NS }),
      hasRole: hasRole(U3208ROLES.ADD),
      handleClick: () => formStateHandlers.addNewCorpus(),
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: 'modify',
      label: t('btnModify', { ns: NS }),
      hasRole: hasRole(U3208ROLES.EDIT),
      handleClick: () => formStateHandlers.modifyCorpus(),
      disabled: formState === U2000FormStateEnumState.EDIT || !canEditCorpus,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: 'delete',
      label: t('btnDelete', { ns: NS }),
      hasRole: hasRole(U3208ROLES.DELETE),
      handleClick: () => formStateHandlers.deleteCorpus(),
      disabled: formState === U2000FormStateEnumState.EDIT || !canDeleteCorpus,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
